// Das Stichwort-Suchfeld (DESIGN-Vorgabe 15.10.2026, wegen rund 2.000
// cobra-Begriffen): bis 20 Begriffe bleibt die Auswahl ein Select; darueber
// wird sie ein Suchfeld mit Serversuche. Ein Baustein fuer drei Orte: die
// Facette der Kontaktliste (Quelle S0, dieselbe Regel wie die Suche, kein
// toter Vorschlag), die Vergabe am Kontakt und die Massenpflege (Quelle
// Katalogsuche mit assignableOnly). Die Quelle ist deshalb ein Parameter.
//
// Combobox nach APG ueber useCombobox, mit Vormerkung fuer Tab (C+): Enter
// waehlt nur eine per Pfeil aktive Option. Leer zeigt das Feld die ersten 20
// der geladenen Liste, ab dem ersten Zeichen fragt es die Quelle, entprellt.

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import {
  Button,
  Caption1,
  Field,
  Input,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";

import { useCombobox } from "../shell/use-combobox.ts";
import { useEntprellterAbruf } from "../shell/use-entprellter-abruf.ts";
import { Tasten } from "../shell/taste.tsx";
import { MAXIMUM_TERM_QUERY_LENGTH } from "./aufloesung.ts";
import { listenHinweis, listenSatz, SELECT_HOECHSTENS } from "./stichwort-saetze.ts";

export { SELECT_HOECHSTENS };

const VERZOEGERUNG_MS = 250;

export interface StichwortEintrag {
  readonly id: string;
  readonly name: string;
  /** Liste bzw. Achse, damit gleichnamige Begriffe unterscheidbar bleiben. */
  readonly achse: string;
  /** Der fruehere Name, ueber den das Getippte traf; sonst stuende ein scheinbar fremder Begriff da. */
  readonly frueher?: string;
  /** Wann der Begriff vergeben wird, wo die Quelle es liefert. */
  readonly beschreibung?: string | null | undefined;
}

export type StichwortTreffer =
  | {
      readonly art: "geladen";
      readonly eintraege: readonly StichwortEintrag[];
      readonly gekuerzt: boolean;
    }
  | { readonly art: "gestoert" };

/** Ob die geladene erste Seite ein Suchfeld verlangt: mehr als 20 oder unvollstaendig. */
export function brauchtSuchfeld(anzahl: number, gekuerzt: boolean): boolean {
  return gekuerzt || anzahl > SELECT_HOECHSTENS;
}

/** Zerlegt den Namen um den ersten Treffer des getippten Teils, ohne Gross/Klein. */
export function trefferTeile(
  name: string,
  getippt: string,
): { readonly vor: string; readonly treffer: string; readonly nach: string } {
  const suche = getippt.trim().toLocaleLowerCase("de-DE");
  const stelle =
    suche === "" ? -1 : name.toLocaleLowerCase("de-DE").indexOf(suche);
  if (stelle < 0) return { vor: name, treffer: "", nach: "" };
  return {
    vor: name.slice(0, stelle),
    treffer: name.slice(stelle, stelle + suche.length),
    nach: name.slice(stelle + suche.length),
  };
}

const useStyles = makeStyles({
  rahmen: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
    minWidth: 0,
  },
  liste: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    zIndex: 10,
    marginTop: "0.25rem",
    padding: "0.25rem",
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
  },
  // Gescrollt wird in der Listbox selbst: sie ist ueber das Feld bedienbar
  // (aria-activedescendant), ein scrollender Rahmen ohne Fokusziel waere es nicht.
  listbox: { maxHeight: "18rem", overflowY: "auto" },
  option: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: "0.75rem",
    padding: "0.5rem 0.75rem",
    minHeight: "2.75rem",
    boxSizing: "border-box",
    borderRadius: tokens.borderRadiusMedium,
    cursor: "pointer",
    overflowWrap: "anywhere",
    '&[aria-selected="true"]': {
      backgroundColor: tokens.colorBrandBackground2,
      color: tokens.colorBrandForeground2,
      boxShadow: `inset 3px 0 0 ${tokens.colorBrandStroke1}`,
    },
  },
  vorgemerkt: { backgroundColor: tokens.colorNeutralBackground1Hover },
  veraltet: { color: tokens.colorNeutralForeground2, cursor: "progress" },
  frueher: { display: "block", color: tokens.colorNeutralForeground2 },
  rechts: {
    display: "inline-flex",
    alignItems: "baseline",
    gap: "0.5rem",
    flexShrink: 0,
  },
  achse: { color: tokens.colorNeutralForeground2 },
  hinweis: {
    display: "block",
    padding: "0.5rem 0.75rem",
    color: tokens.colorNeutralForeground2,
  },
  gewaehlt: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.25rem 0.5rem",
    margin: 0,
    padding: 0,
    listStyle: "none",
  },
  nurLeser: {
    position: "absolute",
    width: "1px",
    height: "1px",
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    whiteSpace: "nowrap",
  },
});

export function StichwortSuchfeld(props: {
  readonly label: string;
  /** Ein Satz der Aufrufenden ueber dem eigenen Hinweis, etwa die Grenze der Auswahl. */
  readonly hinweis?: string | undefined;
  /** Fuer Aufrufende, die das Feld nach einem Fehler oder beim Oeffnen fokussieren. */
  readonly eingabeId?: string | undefined;
  /** Feldfehler der Aufrufenden, etwa „nichts gewaehlt“; er steht am Feld. */
  readonly fehler?: string | undefined;
  /** Eine Wahl statt mehrerer: die neue ersetzt die alte, und die Ansage sagt es. */
  readonly einzeln?: boolean | undefined;
  /** Waehrend die Aufrufenden arbeiten, sind Feld und Chips gesperrt. */
  readonly gesperrt?: boolean | undefined;
  /** Die geladene erste Seite der vergebbaren Begriffe. */
  readonly ersteSeite: readonly StichwortEintrag[];
  readonly ersteSeiteGekuerzt: boolean;
  readonly suchen: (
    getippt: string,
    signal: AbortSignal,
  ) => Promise<StichwortTreffer>;
  readonly gewaehlt: readonly StichwortEintrag[];
  /** false: nicht angenommen (etwa die Grenze); das Getippte bleibt, die Region nennt den Grund. */
  readonly onHinzufuegen: (eintrag: StichwortEintrag) => boolean;
  /** Der Satz, den die Region bei einer abgelehnten Aufnahme sagt. */
  readonly abgelehntSatz?: string;
  readonly onEntfernen: (id: string) => void;
}): ReactElement {
  const own = useStyles();
  const listId = useId();
  const idPraefix = `${useId()}-`;
  const [getippt, setGetippt] = useState("");
  const [offen, setOffen] = useState(false);
  const [verlangt, setVerlangt] = useState(false);
  const [getipptSeitWahl, setGetipptSeitWahl] = useState(false);
  const [ansage, setAnsage] = useState("");
  // Zaehlt "noch einmal versuchen": Fokus oder Pfeil nach einer Stoerung fragt neu.
  const [wiederholung, setWiederholung] = useState(0);
  const { suchen } = props;

  // Je Suchtext gemerkt, solange das Feld steht: wer zuruecktippt, fragt nicht neu (CM-SEA-013).
  const speicher = useRef(new Map<string, StichwortTreffer>());
  useEffect(() => {
    speicher.current = new Map();
  }, [suchen]);
  const abrufen = useCallback(
    async (anfrage: string, signal: AbortSignal): Promise<StichwortTreffer> => {
      const schluessel = anfrage.toLocaleLowerCase("de-DE");
      const gemerkt = speicher.current.get(schluessel);
      if (gemerkt !== undefined) return gemerkt;
      const treffer = await suchen(anfrage, signal);
      if (treffer.art === "geladen") speicher.current.set(schluessel, treffer);
      return treffer;
    },
    [suchen],
  );
  const anfrage = getippt.trim() === "" ? undefined : getippt.trim();
  const stand = useEntprellterAbruf({
    anfrage,
    verzoegerungMs: VERZOEGERUNG_MS,
    abrufen,
    wiederholung,
  });
  const gewaehltIds = useMemo(
    () => new Set(props.gewaehlt.map((eintrag) => eintrag.id)),
    [props.gewaehlt],
  );

  const antwort =
    anfrage === undefined
      ? undefined
      : stand.zustand === "fertig" || stand.zustand === "laedt"
        ? stand.antwort
        : undefined;
  const gestoert =
    anfrage !== undefined &&
    (stand.zustand === "fehler" || antwort?.art === "gestoert");
  const quelle =
    anfrage === undefined
      ? props.ersteSeite.slice(0, SELECT_HOECHSTENS)
      : antwort?.art === "geladen"
        ? antwort.eintraege
        : [];
  const optionen = quelle.filter((eintrag) => !gewaehltIds.has(eintrag.id));
  // Waehrend das Getippte laedt, steht die vorige Antwort noch da, damit nichts
  // springt; waehlbar ist sie nicht, sonst kaeme ein Begriff zu einem alten Text.
  const veraltet = anfrage !== undefined && stand.zustand !== "fertig";
  const hinweis = listenHinweis({
    getippt,
    anzahl: optionen.length,
    geliefert: quelle.length,
    gekuerzt: antwort?.art === "geladen" && antwort.gekuerzt,
    ersteSeiteMehr: brauchtSuchfeld(
      props.ersteSeite.length,
      props.ersteSeiteGekuerzt,
    ),
    gestoert,
  });
  // Ohne Antwort auf das Getippte bleibt die Liste zu, statt kurz „kein Treffer“ zu zeigen.
  const wartet = anfrage !== undefined && antwort === undefined && !gestoert;
  const listeOffen =
    offen && !wartet && (optionen.length > 0 || hinweis !== undefined);

  const combobox = useCombobox({
    optionen,
    schluessel: (eintrag) => eintrag.id,
    idPraefix,
    listId,
    offen: listeOffen && optionen.length > 0,
    ersteIstAktiv: verlangt,
    // Nur aus der Antwort auf das jetzt Getippte; waehrend sie laedt, steht die vorige noch da.
    vormerken: getipptSeitWahl && getippt.trim() !== "" && stand.zustand === "fertig",
    onWaehlen: (eintrag, weg) => {
      if (veraltet) return;
      if (!props.onHinzufuegen(eintrag)) {
        setOffen(false);
        setAnsage(props.abgelehntSatz ?? `„${eintrag.name}“ wurde nicht übernommen.`);
        return;
      }
      setGetippt("");
      setVerlangt(false);
      setGetipptSeitWahl(false);
      setOffen(false);
      const vorher = props.einzeln === true ? props.gewaehlt[0] : undefined;
      setAnsage(
        props.einzeln === true
          ? vorher === undefined
            ? `„${eintrag.name}“ gewählt.`
            : `„${eintrag.name}“ gewählt statt „${vorher.name}“.`
          : weg === "tab"
            ? `„${eintrag.name}“ übernommen.`
            : `„${eintrag.name}“ hinzugefügt.`,
      );
    },
    onEscape: () => {
      setOffen(false);
      setVerlangt(false);
      setAnsage("");
    },
  });

  // Eine Ansage fuer die offene Liste: vorne Zahl oder Grenze, dahinter die
  // Vormerkung (C+). Entprellt, und nur wenn sich der Satz aendert.
  const listenAnsage = listeOffen
    ? listenSatz({
        getippt,
        anzahl: optionen.length,
        hinweis,
        vorgemerkt: combobox.vorgemerkt?.name,
      })
    : undefined;
  useEffect(() => {
    if (listenAnsage === undefined) return undefined;
    const zeitgeber = setTimeout(() => setAnsage(listenAnsage), VERZOEGERUNG_MS);
    return () => clearTimeout(zeitgeber);
  }, [listenAnsage]);

  // Nach dem Entfernen steht der Fokus auf dem naechsten Chip, beim letzten
  // auf dem vorigen, ohne Chips im Feld -- nie auf BODY.
  const rahmen = useRef<HTMLDivElement>(null);
  const [fokusNach, setFokusNach] = useState<number | undefined>(undefined);
  useEffect(() => {
    if (fokusNach === undefined) return;
    const chips = rahmen.current?.querySelectorAll<HTMLButtonElement>("[data-stichwort-chip]") ?? [];
    const ziel = chips.length === 0 ? rahmen.current?.querySelector("input") : chips[Math.min(fokusNach, chips.length - 1)];
    ziel?.focus();
    setFokusNach(undefined);
  }, [fokusNach, props.gewaehlt]);

  return (
    <Field
      label={props.label}
      {...(props.hinweis === undefined ? {} : { hint: props.hinweis })}
      validationMessage={props.fehler ?? null}
      validationState={props.fehler === undefined ? "none" : "error"}
    >
      <div className={own.rahmen} ref={rahmen}>
        <Input
          {...(props.eingabeId === undefined ? {} : { id: props.eingabeId })}
          disabled={props.gesperrt === true}
          value={getippt}
          autoComplete="off"
          placeholder="Stichwort suchen"
          input={{
            ...combobox.eingabeAttribute,
            onPointerDown: () => setOffen(true),
            // Die Grenze der Quelle: laenger lehnte sie jede Anfrage ab (422).
            maxLength: MAXIMUM_TERM_QUERY_LENGTH,
            onBlur: () => {
              setOffen(false);
              setAnsage("");
            },
          }}
          // Nach APG oeffnet die Liste beim Tippen, bei Pfeil ab und beim Klick ins
          // Feld, nicht beim blossen Fokus: sonst laege sie nach einem gesetzten
          // Fokus ueber dem, was darunter steht. Nach einer Stoerung fragt der Fokus neu.
          onFocus={() => {
            if (gestoert) setWiederholung((n) => n + 1);
          }}
          onKeyDown={(ereignis) => {
            // Nach einer Stoerung steht der Satz in der offenen Liste; Pfeil ab fragt dann trotzdem neu.
            // Alt+Pfeil ab oeffnet nur, ohne die erste Option zu waehlen (APG).
            if (ereignis.key === "ArrowDown" && (!listeOffen || gestoert)) {
              ereignis.preventDefault();
              setOffen(true);
              // Auch ein frueheres Pfeil ab vergisst Alt+Pfeil ab: es oeffnet ohne Auswahl.
              setVerlangt(!ereignis.altKey);
              if (gestoert) setWiederholung((n) => n + 1);
              return;
            }
            combobox.beiTaste(ereignis);
            // Tab ohne Uebernahme schliesst die Liste noch im Tastendruck: Chrome macht
            // die scrollbare Listbox sonst selbst tabbar, sie haengt beim Blur aus, und
            // der Fokus fiele auf BODY. Ausgehaengt springt der Browser zum naechsten Element.
            if (ereignis.key === "Tab" && !ereignis.defaultPrevented) setOffen(false);
          }}
          onChange={(_ereignis, data) => {
            setGetippt(data.value);
            setOffen(true);
            setVerlangt(false);
            setGetipptSeitWahl(true);
            combobox.zuruecksetzen();
          }}
        />
        {listeOffen ? (
          <div className={own.liste}>
            {optionen.length > 0 ? (
              <div
                id={listId}
                role="listbox"
                aria-label={`${props.label}: Vorschläge`}
                className={own.listbox}
              >
                {optionen.map((eintrag) => {
                  const teile = trefferTeile(eintrag.name, getippt);
                  const alt = eintrag.frueher === undefined ? undefined : trefferTeile(eintrag.frueher, getippt);
                  return (
                    <div
                      key={eintrag.id}
                      {...combobox.optionAttribute(eintrag)}
                      {...(veraltet ? { "aria-disabled": true } : {})}
                      className={mergeClasses(
                        own.option,
                        eintrag === combobox.vorgemerkt
                          ? own.vorgemerkt
                          : undefined,
                        veraltet ? own.veraltet : undefined,
                      )}
                    >
                      <span>
                        {teile.vor}
                        {teile.treffer === "" ? undefined : (
                          <strong>{teile.treffer}</strong>
                        )}
                        {teile.nach}
                        <span style={{ fontSize: 0 }}>,</span>
                        {/* Derselbe Satz wie im Stichwortkatalog; der Treffer steht im frueheren Namen. */}
                        {alt === undefined ? undefined : (
                          <Caption1 className={own.frueher}>
                            Gefunden unter dem früheren Namen „{alt.vor}
                            {alt.treffer === "" ? undefined : <strong>{alt.treffer}</strong>}
                            {alt.nach}“.
                            <span style={{ fontSize: 0 }}>,</span>
                          </Caption1>
                        )}
                      </span>
                      <span className={own.rechts}>
                        <Caption1 className={own.achse}>
                          {eintrag.achse}
                        </Caption1>
                        {eintrag === combobox.vorgemerkt ? (
                          <Tasten folge="Tab" stumm />
                        ) : undefined}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : undefined}
            {hinweis === undefined ? undefined : (
              <Caption1 className={own.hinweis}>{hinweis}</Caption1>
            )}
          </div>
        ) : undefined}
        {/* Die Region haengt leer mit der Liste ein und steht nur, solange sie
            gebraucht wird: im Ruhezustand spricht nur die Statusregion der Seite. */}
        {listeOffen || ansage !== "" ? (
          <div
            role="status"
            aria-live="polite"
            aria-label={`Status: ${props.label}`}
            className={own.nurLeser}
          >
            {ansage}
          </div>
        ) : undefined}
        {props.gewaehlt.length > 0 ? (
          <ul className={own.gewaehlt} aria-label={`Gewählt: ${props.label}`}>
            {props.gewaehlt.map((eintrag, stelle) => (
              <li key={eintrag.id}>
                <Button
                  type="button"
                  size="small"
                  appearance="secondary"
                  // Der Name beginnt mit dem sichtbaren Text (WCAG 2.5.3); die Achse
                  // trennt gleichnamige Begriffe, das Kreuz ist Schmuck.
                  aria-label={
                    eintrag.achse === ""
                      ? `${eintrag.name} entfernen`
                      : `${eintrag.name} (${eintrag.achse}) entfernen`
                  }
                  data-stichwort-chip=""
                  disabled={props.gesperrt === true}
                  onClick={() => {
                    props.onEntfernen(eintrag.id);
                    setFokusNach(stelle);
                  }}
                >
                  {eintrag.name}
                  <span aria-hidden="true">&nbsp;×</span>
                </Button>
              </li>
            ))}
          </ul>
        ) : undefined}
      </div>
    </Field>
  );
}
